import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Quill from 'quill';
import clsx from 'clsx';
import {
  getContent,
  highlightSearchTerm,
  hasSpecialTags,
  calculateTextLength,
  getTableMetadata,
  getTableEntryCounts,
  filterEntriesByTables,
  deduplicateTextEntries,
  isDeduplicatedEntry,
  sortEntriesByHierarchyDFS
} from '../../utils';
import { convertTMPToQuill } from '../../tmp-quill-converter';
import { TableFilterSidebar } from './TableFilterSidebar';
import { HoverPopover } from './HoverPopover';
import { LoadingOverlay } from './LoadingOverlay';
import { referenceSheetManager } from '../../services/reference-sheet-manager';
import '../../quill-modules/quill-autocomplete';
import type {
  EditTabProps,
  TextEntry,
  DeduplicatedTextEntry,
  QuillInstance,
  SearchResult
} from '../../types';

const EditTab: React.FC<EditTabProps> = ({
  modalState,
  csvData,
  lastReceivedJson,
  quillInstances,
  languageOptions,
  onStateChange,
  onSaveChanges,
  onQuillChange,
  isLoadingTemplateValues = false,
  loadingStatus = ''
}) => {
  const [displayedEntries, setDisplayedEntries] = useState<(TextEntry | DeduplicatedTextEntry)[]>([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [searchFilteredCount, setSearchFilteredCount] = useState(0);
  const [tableFilteredCount, setTableFilteredCount] = useState(0);
  const [displayLimited, setDisplayLimited] = useState(false);
  const [hasSpecialTagsInContent, setHasSpecialTagsInContent] = useState(false);
  const [localSearchTerm, setLocalSearchTerm] = useState(modalState.searchTerm);
  const [useDeduplication, setUseDeduplication] = useState(true);

  const editorContainerRef = useRef<HTMLDivElement>(null);

  // Debounce search term updates to parent state
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearchTerm !== modalState.searchTerm) {
        onStateChange({ searchTerm: localSearchTerm });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [localSearchTerm]);

  // Sync local search term when parent state changes externally
  useEffect(() => {
    setLocalSearchTerm(modalState.searchTerm);
  }, [modalState.searchTerm]);

  const hasMultipleLanguages = languageOptions.length > 1;
  const translateModeDisabledMessage = 'Only one language configured in app-config.js';

  // Parse Unity data
  const unityData = useMemo(() => {
    if (!lastReceivedJson) return null;
    try {
      return JSON.parse(lastReceivedJson);
    } catch {
      return null;
    }
  }, [lastReceivedJson]);


  // Get table metadata and counts
  const tableMetadata = useMemo(() => {
    return getTableMetadata(unityData);
  }, [unityData]);

  const tableCounts = useMemo(() => {
    if (!unityData?.textEntries) return {};
    return getTableEntryCounts(unityData.textEntries, tableMetadata);
  }, [unityData?.textEntries, tableMetadata]);

  // Auto-select all tables when Unity data first arrives
  useEffect(() => {
    if (unityData?.textEntries && Object.keys(tableMetadata).length > 0) {
      // Only auto-select if no tables are currently selected
      if (modalState.selectedTables.size === 0) {
        const allTableNames = Object.keys(tableMetadata);
        const newSelectedTables = new Set(allTableNames);
        onStateChange({
          selectedTables: newSelectedTables,
          showAllTables: true
        });
      }
    }
  }, [unityData?.textEntries?.length, Object.keys(tableMetadata).length]);

  // Keep selected languages in sync with configured options
  useEffect(() => {
    const languages = languageOptions;
    const updates: Partial<typeof modalState> = {};

    if (languages.length === 0) {
      if (modalState.standardLanguage) {
        updates.standardLanguage = '';
      }
      if (modalState.languageA) {
        updates.languageA = '';
      }
      if (modalState.languageB) {
        updates.languageB = '';
      }
      if (modalState.currentMode !== 'standard') {
        updates.currentMode = 'standard';
      }
    } else {
      const primary = languages[0];
      const secondary = languages.length > 1 ? languages[1] : primary;

      if (!languages.includes(modalState.standardLanguage)) {
        updates.standardLanguage = primary;
      }
      if (!languages.includes(modalState.languageA)) {
        updates.languageA = primary;
      }
      if (!languages.includes(modalState.languageB)) {
        updates.languageB = secondary;
      }

      if (languages.length < 2 && modalState.currentMode !== 'standard') {
        updates.currentMode = 'standard';
      }
    }

    if (Object.keys(updates).length > 0) {
      onStateChange(updates);
    }
  }, [languageOptions, modalState.standardLanguage, modalState.languageA, modalState.languageB, modalState.currentMode, onStateChange]);

  // Process and filter entries
  useEffect(() => {
    if (!unityData?.textEntries) {
      setDisplayedEntries([]);
      setTotalEntries(0);
      return;
    }

    let entries: (TextEntry | DeduplicatedTextEntry)[] = [...unityData.textEntries];

    // Apply table filters first
    let tableFilteredEntries = entries;
    let tableFiltered = 0;

    const selectedTables = modalState.selectedTables ?? new Set<string>();

    if (selectedTables.size > 0) {
      tableFilteredEntries = filterEntriesByTables(entries as TextEntry[], selectedTables, tableMetadata);
      tableFiltered = entries.length - tableFilteredEntries.length;
    } else {
      // When no tables selected, show nothing
      tableFilteredEntries = [];
      tableFiltered = entries.length;
    }

    setTableFilteredCount(tableFiltered);

    // Apply deduplication if enabled
    if (useDeduplication) {
      entries = deduplicateTextEntries(tableFilteredEntries as TextEntry[]);
    } else {
      entries = tableFilteredEntries;
    }

    // Set total AFTER deduplication to reflect actual unique entries available
    setTotalEntries(entries.length);

    // Calculate text length and object name for each entry
    entries.forEach(entry => {
      if (isDeduplicatedEntry(entry)) {
        // For deduplicated entries, use first path for object name
        const pathParts = entry.gameObjectPaths[0].split(' > ');
        entry.objectName = pathParts[pathParts.length - 1];
        entry.sortTextLength = calculateTextLength({ text: entry.text } as TextEntry);
      } else {
        // For regular entries
        const pathParts = entry.path.split(' > ');
        entry.objectName = pathParts[pathParts.length - 1];
        entry.sortTextLength = calculateTextLength(entry);
      }
    });

    // Sort entries
    switch (modalState.sortOrder) {
      case 'size-desc':
        entries.sort((a, b) => (b.sortTextLength || 0) - (a.sortTextLength || 0));
        break;
      case 'size-asc':
        entries.sort((a, b) => (a.sortTextLength || 0) - (b.sortTextLength || 0));
        break;
      case 'alpha':
        entries.sort((a, b) => (a.objectName || '').localeCompare(b.objectName || ''));
        break;
      case 'hierarchy':
        // Reversed DFS order with sibling groups reversed
        // Step 1: Get reversed DFS order (deepest first)
        sortEntriesByHierarchyDFS(entries);

        // Step 2: Reverse each consecutive group of siblings
        // Helper to get parent path from a full path
        const getParentPath = (path: string): string => {
          const parts = path.split(' > ');
          if (parts.length <= 1) return '';
          return parts.slice(0, -1).join(' > ');
        };

        // Helper to get path from entry
        const getPath = (entry: typeof entries[0]): string => {
          return isDeduplicatedEntry(entry)
            ? (entry.gameObjectPaths?.[0] || '')
            : (entry.path || '');
        };

        // Find consecutive groups of siblings and reverse each group
        let i = 0;
        while (i < entries.length) {
          const currentPath = getPath(entries[i]);
          const currentParent = getParentPath(currentPath);

          // Find the end of this sibling group
          let j = i + 1;
          while (j < entries.length && getParentPath(getPath(entries[j])) === currentParent) {
            j++;
          }

          // Reverse this group of siblings in place
          if (j > i + 1) {
            const siblingGroup = entries.slice(i, j);
            siblingGroup.reverse();
            entries.splice(i, j - i, ...siblingGroup);
          }

          i = j;
        }
        break;
      case 'hierarchy-reversed':
        // Reversed DFS order (bottom-up, deepest first) - old behavior
        sortEntriesByHierarchyDFS(entries);
        break;
    }

    // Apply search filter to table-filtered entries
    let filteredEntries = entries;
    let searchFiltered = 0;

    if (modalState.searchTerm) {
      const term = modalState.searchTerm.toLowerCase();

      const searchResults = entries.map(entry => {
        let score = 0;
        let pathHighlighted = '';
        let contentHighlighted = '';

        if (isDeduplicatedEntry(entry)) {
          // For deduplicated entries, search across all paths
          const allPaths = entry.gameObjectPaths.join(' ');
          if (allPaths.toLowerCase().includes(term)) {
            score += 10;
            pathHighlighted = highlightSearchTerm(entry.gameObjectPaths[0], term);
          }

          // Check content match
          const content = entry.text || '';
          if (content.toLowerCase().includes(term)) {
            score += 15;
            contentHighlighted = highlightSearchTerm(content, term);
          }
        } else {
          // For regular entries
          if (entry.path.toLowerCase().includes(term)) {
            score += 10;
            pathHighlighted = highlightSearchTerm(entry.path, term);
          }

          const content = getContent(entry);
          if (content.toLowerCase().includes(term)) {
            score += 15;
            contentHighlighted = highlightSearchTerm(content, term);
          }
        }

        return { entry, searchScore: score, pathHighlighted, contentHighlighted };
      });
      
      // Filter and sort by score
      const matches = searchResults.filter(r => r.searchScore > 0);
      matches.sort((a, b) => b.searchScore - a.searchScore);
      
      filteredEntries = matches.map(r => {
        r.entry.pathHighlighted = r.pathHighlighted;
        r.entry.contentHighlighted = r.contentHighlighted;
        return r.entry;
      });
      
      searchFiltered = entries.length - filteredEntries.length;
    }
    
    setSearchFilteredCount(searchFiltered);
    
    // Apply display limit
    const limited = filteredEntries.length > modalState.numFields;
    setDisplayLimited(limited);
    
    const displayed = filteredEntries.slice(0, modalState.numFields);
    setDisplayedEntries(displayed);
    
    // Check for special tags
    setTimeout(() => {
      let hasSpecial = false;
      Object.values(quillInstances).forEach(instance => {
        if (instance?.quill) {
          const content = instance.quill.root.innerHTML;
          if (hasSpecialTags(content)) {
            hasSpecial = true;
          }
        }
      });
      setHasSpecialTagsInContent(hasSpecial);
    }, 200);

  }, [unityData, modalState, csvData, quillInstances, tableMetadata, useDeduplication]);

  // Initialize Quill editors after entries are displayed
  useEffect(() => {
    if (displayedEntries.length === 0) return;

    // Save the currently focused element
    const activeElement = document.activeElement as HTMLElement;
    const isSearchInputFocused = activeElement?.closest('.flex-1.relative') !== null;

    // Clean up existing instances
    Object.keys(quillInstances).forEach(id => {
      const instance = quillInstances[id];
      if (instance?.quill) {
        // Remove all event listeners
        instance.quill.off('text-change');
        instance.quill.off('selection-change');
        instance.quill.off('editor-change');
        // Clear the editor properly
        const container = instance.quill.root.parentElement;
        if (container && container.parentElement) {
          // Remove the toolbar first
          const toolbar = container.parentElement.querySelector('.quill-toolbar');
          if (toolbar) toolbar.remove();
          // Then clear the editor container
          container.innerHTML = '';
        }
      }
      delete quillInstances[id];
    });

    // Initialize new editors
    setTimeout(() => {
      displayedEntries.forEach((entry, index) => {
        if (modalState.currentMode === 'standard') {
          initializeQuillEditor(`editor-${index}`, entry, modalState.standardLanguage);
        } else {
          initializeQuillEditor(`editor-${index}-left`, entry, modalState.languageA);
          initializeQuillEditor(`editor-${index}-right`, entry, modalState.languageB);
        }
      });

      // Restore focus to search input if it was focused before
      if (isSearchInputFocused && activeElement) {
        setTimeout(() => {
          activeElement.focus();
        }, 0);
      }
    }, 100);

  }, [displayedEntries, modalState.currentMode, modalState.standardLanguage, modalState.languageA, modalState.languageB]);

  const initializeQuillEditor = useCallback((
    editorId: string,
    entry: TextEntry | DeduplicatedTextEntry,
    language: string
  ) => {
    const element = document.getElementById(editorId);
    if (!element) return;

    const wrapperDiv = element.parentNode as HTMLElement;
    if (!wrapperDiv) return;

    const toolbarId = `toolbar-${editorId}`;

    // Check if entry is missing from Google Sheets
    const isMissingFromSheets = entry.missingFromSheets === true;

    // Create toolbar if it doesn't exist
    if (!document.getElementById(toolbarId)) {
      const toolbarHTML = `
        <div id="${toolbarId}" class="quill-toolbar">
          <span class="ql-formats">
            <button class="ql-bold"></button>
            <button class="ql-italic"></button>
            <button class="ql-underline"></button>
          </span>
          <span class="ql-formats">
            <button class="ql-script" value="sub"></button>
            <button class="ql-script" value="super"></button>
          </span>
          <span class="ql-formats">
            <button class="ql-list" value="ordered"></button>
            <button class="ql-list" value="bullet"></button>
          </span>
          <span class="ql-formats">
            <select class="ql-color"></select>
            <select class="ql-background"></select>
          </span>
          <span class="ql-formats">
            <button class="ql-clean"></button>
          </span>
        </div>
      `;
      wrapperDiv.insertAdjacentHTML('afterbegin', toolbarHTML);
    }

    // Initialize Quill with readOnly if missing from sheets
    const quill = new Quill(`#${editorId}`, {
      theme: 'snow',
      placeholder: isMissingFromSheets ? 'Key not found in Google Sheets' : `Edit text for ${language}...`,
      readOnly: isMissingFromSheets,
      modules: {
        toolbar: isMissingFromSheets ? false : `#${toolbarId}`,
        clipboard: {
          matchVisual: false
        },
        autocomplete: isMissingFromSheets ? false : {
          trigger: '[',
          dataSource: async () => {
            try {
              return await referenceSheetManager.getAllReferences();
            } catch (error) {
              console.error('Failed to load references for autocomplete:', error);
              return [];
            }
          }
        }
      },
      formats: ['bold', 'italic', 'underline', 'list', 'header', 'color', 'background', 'script', 'strike', 'link']
    });

    // Set initial content - use language-specific template value if available
    let content = '';
    if (entry.templateValuesByLanguage && language in entry.templateValuesByLanguage) {
      // Use the template value for the requested language (even if it's an empty string)
      content = entry.templateValuesByLanguage[language];
      console.log(`[EDIT TAB] Using ${language} template value for entry:`, entry.key, content);
    } else {
      // Fallback to default content if language-specific value not available
      content = isDeduplicatedEntry(entry) ? (entry.text || '') : getContent(entry);
      console.warn(`[EDIT TAB] No ${language} template value found for entry, using fallback:`, entry.key);
    }
    const convertedContent = convertTMPToQuill(content);

    if (convertedContent) {
      quill.clipboard.dangerouslyPasteHTML(convertedContent);
    }

    // Store instance - for deduplicated entries, store the first original entry for compatibility
    const storeEntry = isDeduplicatedEntry(entry) ? entry.originalEntries[0] : entry;
    const instance: QuillInstance = {
      quill,
      entry: storeEntry as TextEntry,
      originalText: quill.root.innerHTML,
      originalTMPText: content,
      language // Add the language field
    };

    // Store reference to all original entries if deduplicated (for save logic)
    if (isDeduplicatedEntry(entry)) {
      (instance as any).deduplicatedEntry = entry;
    }

    // Store flag if missing from sheets
    if (isMissingFromSheets) {
      (instance as any).missingFromSheets = true;
    }

    quillInstances[editorId] = instance;

    // Track changes (only if not disabled)
    if (!isMissingFromSheets) {
      quill.on('text-change', () => {
        // Notify parent that there might be changes
        if (onQuillChange) {
          // Use setTimeout to avoid React state update during render
          setTimeout(() => onQuillChange(), 0);
        }
      });
    }
  }, [csvData, quillInstances, onSaveChanges]);

  return (
    <div className="flex h-full w-full overflow-hidden">
      {/* Table Filter Sidebar */}
      {unityData?.tableInfos && modalState.selectedTables && (
        <TableFilterSidebar
          modalState={modalState}
          tableInfos={tableMetadata}
          tableCounts={tableCounts}
          onStateChange={onStateChange}
        />
      )}

      {/* Main Content Area */}
      <div className="flex flex-col flex-1 h-full overflow-hidden">
        {/* Toolbar */}
      <div className="flex items-center gap-4 px-6 py-2.5 border-b border-unity-gray bg-white flex-wrap">
        {/* Display Count */}
        <div className="flex items-center gap-2">
          <label htmlFor="num-fields" className="text-sm text-gray-700 whitespace-nowrap">Display:</label>
          <input
            id="num-fields"
            type="number"
            min="1"
            value={modalState.numFields}
            onChange={(e) => onStateChange({ numFields: parseInt(e.target.value) || 50 })}
            className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
          />
          <span className="text-sm text-gray-700">fields</span>
        </div>

        <div className="w-px h-7 bg-unity-gray" />

        {/* Sort Order */}
        <div className="flex items-center gap-2">
          <label htmlFor="sort-order" className="text-sm text-gray-700 whitespace-nowrap">Sort by:</label>
          <select
            id="sort-order"
            value={modalState.sortOrder}
            onChange={(e) => onStateChange({ sortOrder: e.target.value as any })}
            className="px-2 py-1 border border-gray-300 rounded text-sm min-w-[80px]"
          >
            <option value="size-desc">Text Size (Largest First)</option>
            <option value="size-asc">Text Size (Smallest First)</option>
            <option value="alpha">Object Name (A-Z)</option>
            <option value="hierarchy">Unity Hierarchy</option>
            <option value="hierarchy-reversed">Unity Hierarchy (Reversed)</option>
          </select>
        </div>

        <div className="w-px h-7 bg-unity-gray" />

        {/* Mode Toggle */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-700">Mode:</label>
          <div className="flex border border-gray-300 rounded-md overflow-hidden">
            <button
              onClick={() => onStateChange({ currentMode: 'standard' })}
              className={clsx(
                "px-3 py-1 text-sm transition-colors",
                modalState.currentMode === 'standard'
                  ? "bg-unity-blue text-white"
                  : "bg-white text-gray-700 hover:bg-gray-100"
              )}
            >
              Standard
            </button>
            <button
              disabled={!hasMultipleLanguages}
              onClick={() => {
                if (hasMultipleLanguages) {
                  onStateChange({ currentMode: 'translate' });
                }
              }}
              className={clsx(
                "px-3 py-1 text-sm border-l border-gray-300 transition-colors",
                modalState.currentMode === 'translate' && hasMultipleLanguages
                  ? "bg-unity-blue text-white"
                  : "bg-white text-gray-700 hover:bg-gray-100",
                !hasMultipleLanguages && "bg-gray-100 text-gray-400 cursor-not-allowed hover:bg-gray-100"
              )}
              title={!hasMultipleLanguages ? translateModeDisabledMessage : undefined}
            >
              Translate
            </button>
          </div>
        </div>

        <div className="w-px h-7 bg-unity-gray" />

        {/* Language Selectors - COMMENTED OUT FOR FIXED TURKISH LOCALIZATION */}
        {languageOptions.length > 0 && (
          modalState.currentMode === 'standard' ? (
            <div className="flex items-center gap-2">
              <label htmlFor="standard-lang" className="text-sm text-gray-700">Language:</label>
              <select
                id="standard-lang"
                value={modalState.standardLanguage}
                onChange={(e) => onStateChange({ standardLanguage: e.target.value })}
                className="px-2 py-1 border border-gray-300 rounded text-sm"
              >
                {languageOptions.map(lang => (
                  <option key={lang} value={lang}>{lang}</option>
                ))}
              </select>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <label htmlFor="lang-a" className="text-sm text-gray-700">Left:</label>
              <select
                id="lang-a"
                value={modalState.languageA}
                onChange={(e) => {
                  const newLanguageA = e.target.value;
                  // If user tries to select the same language as right column, swap them
                  if (newLanguageA === modalState.languageB) {
                    onStateChange({
                      languageA: newLanguageA,
                      languageB: modalState.languageA
                    });
                  } else {
                    onStateChange({ languageA: newLanguageA });
                  }
                }}
                className="px-2 py-1 border border-gray-300 rounded text-sm"
              >
                {languageOptions.map(lang => (
                  <option key={lang} value={lang}>{lang}</option>
                ))}
              </select>
              <label htmlFor="lang-b" className="text-sm text-gray-700">Right:</label>
              <select
                id="lang-b"
                value={modalState.languageB}
                onChange={(e) => {
                  const newLanguageB = e.target.value;
                  // If user tries to select the same language as left column, swap them
                  if (newLanguageB === modalState.languageA) {
                    onStateChange({
                      languageB: newLanguageB,
                      languageA: modalState.languageB
                    });
                  } else {
                    onStateChange({ languageB: newLanguageB });
                  }
                }}
                className="px-2 py-1 border border-gray-300 rounded text-sm"
              >
                {languageOptions.map(lang => (
                  <option key={lang} value={lang}>{lang}</option>
                ))}
              </select>
            </div>
          )
        )}

        <div className="w-px h-7 bg-unity-gray" />

        {/* Table Filter Status Indicator */}
        {modalState.selectedTables && modalState.selectedTables.size < Object.keys(tableMetadata).length && (
          <div className="flex items-center gap-2 px-3 py-1 bg-yellow-100 rounded-full border border-yellow-300">
            <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
            <span className="text-xs text-yellow-700 font-medium">
              {modalState.selectedTables.size} of {Object.keys(tableMetadata).length} tables
            </span>
          </div>
        )}

        <div className="w-px h-7 bg-unity-gray" />

        {/* Search */}
        <div className="flex-1 relative">
          <div className="flex items-center border border-gray-300 rounded bg-white px-2 w-full">
            <svg className="w-4 h-4 text-gray-500 min-w-[16px]" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16">
              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
            </svg>
            <input
              type="text"
              placeholder="Search in text content..."
              value={localSearchTerm}
              onChange={(e) => setLocalSearchTerm(e.target.value)}
              className="flex-1 px-2 py-1 outline-none text-sm w-full"
            />
            {localSearchTerm && (
              <button
                onClick={() => setLocalSearchTerm('')}
                className="px-2 py-0.5 bg-red-500 text-white rounded text-xs hover:bg-red-600"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

        {/* Field Count Indicator */}
        <div className={clsx(
          "mx-5 mt-2 px-3 py-2 text-sm bg-unity-gray-light rounded border-l-4",
          (searchFilteredCount > 0 || tableFilteredCount > 0 || displayLimited)
            ? "bg-red-50 border-red-400 text-red-700"
            : displayedEntries.length < totalEntries
            ? "bg-yellow-50 border-yellow-400 text-yellow-700"
            : "border-gray-400 text-gray-700"
        )}>
          Showing {displayedEntries.length} of {totalEntries} entries
          {(tableFilteredCount > 0 || searchFilteredCount > 0 || displayLimited) && (
            <span className="ml-1">
              ({tableFilteredCount + searchFilteredCount + (displayLimited ? (totalEntries - displayedEntries.length - tableFilteredCount - searchFilteredCount) : 0)} hidden due to
              {tableFilteredCount > 0 && ` table filters`}
              {tableFilteredCount > 0 && (searchFilteredCount > 0 || displayLimited) && `,`}
              {searchFilteredCount > 0 && ` search`}
              {(searchFilteredCount > 0 || tableFilteredCount > 0) && displayLimited && `,`}
              {displayLimited && ` display limit`})
            </span>
          )}
        </div>

      {/* Special Tags Warning */}
      {hasSpecialTagsInContent && (
        <div className="mx-5 mt-2 px-3 py-2 bg-orange-100 text-orange-700 rounded text-sm italic border-l-4 border-orange-400">
          <strong>Warning:</strong> Some fields contain [ref] or [indent] tags. Please use caution when modifying them and do not apply rich text formatting to them.
        </div>
      )}

      {/* Editor Container */}
      <div
        ref={editorContainerRef}
        className={clsx(
          "flex-1 overflow-auto p-5",
          isLoadingTemplateValues && "blur-sm pointer-events-none"
        )}
      >
        {displayedEntries.length === 0 ? (
          <div className="text-center py-12">
            {!unityData ? (
              <>
                <div className="w-10 h-10 border-4 border-gray-200 border-t-unity-blue rounded-full animate-spin-slow mx-auto mb-4"></div>
                <p className="text-gray-500">Waiting for text content from Unity...</p>
                <p className="text-xs text-gray-400 mt-2">If this persists, try refreshing the page</p>
              </>
            ) : modalState.selectedTables.size === 0 && totalEntries > 0 ? (
              <>
                <p className="text-gray-500 text-lg font-medium">No tables selected</p>
                <p className="text-xs text-gray-400 mt-2">Select one or more tables from the sidebar to view entries</p>
              </>
            ) : (
              <>
                <p className="text-gray-500 text-lg font-medium">No editable text fields detected from the scene</p>
                <p className="text-xs text-gray-400 mt-2">Make sure LocalizeStringEvent components exist in the loaded scene(s)</p>
              </>
            )}
          </div>
        ) : (
          displayedEntries.map((entry, index) => {
            const entryKey = isDeduplicatedEntry(entry) ? entry.dedupeId : entry.id;
            return (
              <EditorEntry
                key={`${entryKey}-${index}`}
                entry={entry}
                index={index}
                mode={modalState.currentMode}
              />
            );
          })
        )}
        </div>

        {/* Loading Overlay */}
        <LoadingOverlay
          isVisible={isLoadingTemplateValues}
          status={loadingStatus}
        />
      </div>
    </div>
  );
};

const EditorEntry: React.FC<{
  entry: TextEntry | DeduplicatedTextEntry;
  index: number;
  mode: 'standard' | 'translate';
}> = ({ entry, index, mode }) => {
  const isDeduplicated = isDeduplicatedEntry(entry);
  const isMissingFromSheets = entry.missingFromSheets === true;

  // Determine paths to show in popover
  const hasPaths = isDeduplicated ? entry.gameObjectPaths.length > 0 : !!entry.path;
  const pathCount = isDeduplicated ? entry.instanceCount : 1;
  const paths = isDeduplicated ? entry.gameObjectPaths : (entry.path ? [entry.path] : []);

  // Determine runtime values to show in popover
  const hasRuntimeValues = isDeduplicated
    ? !!(entry.runtimeValues && entry.runtimeValues.length > 0) || !!entry.runtimeValue
    : !!entry.runtimeValue;

  const runtimeValues = isDeduplicated
    ? (entry.runtimeValues && entry.runtimeValues.length > 0 ? entry.runtimeValues : (entry.runtimeValue ? [entry.runtimeValue] : []))
    : (entry.runtimeValue ? [entry.runtimeValue] : []);

  return (
    <div className={clsx(
      "mb-5 rounded-lg shadow-sm p-4 flex flex-col gap-2.5 transition-all",
      isMissingFromSheets ? "bg-gray-50 border-2 border-amber-300" : "bg-white"
    )}>
      {/* Missing from Sheets Warning Banner */}
      {isMissingFromSheets && (
        <div className="mb-2 px-4 py-3 bg-amber-50 border-l-4 border-amber-500 rounded-md flex items-start gap-3">
          <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
          </svg>
          <div className="flex-1">
            <div className="font-semibold text-amber-800 text-sm">Key Not Found in Google Sheets</div>
            <div className="text-amber-700 text-xs mt-1">
              This localization key does not exist in the configured Google Sheets. Developers are advised to push from Unity to Google Sheets to sync this entry.
            </div>
          </div>
        </div>
      )}

      {/* Key Display */}
      <div className="pb-2 border-b border-gray-200">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="text-base font-semibold text-unity-gray-dark flex items-center gap-2">
              {/* Show localization key prominently */}
              {entry.key || 'No Key'}

              {/* Instance count badge for deduplicated entries */}
              {isDeduplicated && entry.instanceCount > 1 && (
                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full border border-green-300">
                  {entry.instanceCount} instances
                </span>
              )}
            </div>
          </div>

          {/* Table Name Badge with Popover */}
          {entry.tableName && hasPaths && (
            <HoverPopover
              trigger={
                <div className="px-2 py-1 bg-unity-blue text-white text-xs font-medium rounded-md whitespace-nowrap cursor-help hover:bg-blue-600 transition-colors">
                  {entry.tableName}
                </div>
              }
              content={
                <div>
                  <div className="font-semibold text-gray-900 mb-3 text-base">
                    Table: "{entry.tableName}"
                  </div>

                  <div className="text-xs text-gray-600 mb-2 font-medium">
                    Used in {pathCount} GameObject{pathCount !== 1 ? 's' : ''}:
                  </div>
                  <div className="space-y-1.5 mb-4">
                    {paths.map((path, idx) => (
                      <div key={idx} className="text-xs text-gray-700 p-2 bg-gray-50 rounded border-l-2 border-unity-blue break-all">
                        {path}
                      </div>
                    ))}
                  </div>

                  {hasRuntimeValues && (
                    <>
                      <div className="border-t border-gray-300 pt-3 mt-3">
                        <div className="text-xs text-gray-600 mb-2 font-medium">
                          Runtime Values (from Unity):
                        </div>
                        <div className="space-y-1.5">
                          {runtimeValues.map((value, idx) => (
                            <div key={idx} className="text-xs text-gray-700 p-2 bg-blue-50 rounded border-l-2 border-green-500 break-all">
                              {isDeduplicated && paths[idx] ? (
                                <>
                                  <div className="font-medium text-gray-900 mb-1">"{value}"</div>
                                  <div className="text-gray-500 text-[11px]">at {paths[idx]}</div>
                                </>
                              ) : (
                                <>
                                  <div className="font-medium text-gray-900">"{value}"</div>
                                  {!isDeduplicated && paths[0] && (
                                    <div className="text-gray-500 text-[11px] mt-1">at {paths[0]}</div>
                                  )}
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              }
              width="28rem"
              position="left"
              showDelay={300}
              hideDelay={200}
              persistent={true}
              maxHeight="32rem"
            />
          )}
        </div>
      </div>

      {/* Editor Content */}
      <div className={clsx(
        "flex",
        mode === 'translate' && "gap-4",
        isMissingFromSheets && "opacity-60 pointer-events-none"
      )}>
        {mode === 'standard' ? (
          <div className={clsx(
            "quill-wrapper w-full",
            isMissingFromSheets && "bg-gray-100 border-2 border-dashed border-gray-300 rounded"
          )}>
            <div id={`editor-${index}`} className="quill-editor-container" />
          </div>
        ) : (
          <>
            <div className={clsx(
              "quill-wrapper flex-1",
              isMissingFromSheets && "bg-gray-100 border-2 border-dashed border-gray-300 rounded"
            )}>
              <div id={`editor-${index}-left`} className="quill-editor-container" />
            </div>
            <div className={clsx(
              "quill-wrapper flex-1",
              isMissingFromSheets && "bg-gray-100 border-2 border-dashed border-gray-300 rounded"
            )}>
              <div id={`editor-${index}-right`} className="quill-editor-container" />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export { EditTab };
